import React from "react";
import { createPortal } from "react-dom";
import { X, Upload, BadgeCheck, Star, Download } from "lucide-react";
import { colors, shadows } from "../../theme";
import { refFor } from "../../lib/citations";
import { downloadReferencesHTML, downloadReferencesCSV, inferTier, TIER_META } from "../../lib/referencesExport";
import { styles, MINT } from "./tokens";

export function KbStat({ label, value }) {
  return (
    <div style={styles.kbStat}>
      <div style={styles.kbStatLabel}>{label}</div>
      <div style={styles.kbStatValue}>{value}</div>
    </div>
  );
}
// DATA PROVENANCE — a micro citation tag rendered next to any baseline metric. Resolves the
// backend `cite` string to author/year and links to the source so every number is verifiable.
export function CiteTag({ cite }) {
  const r = refFor(cite);
  let label = cite, title = cite, url = null;
  if (r) {
    if (r.display) { label = r.display; title = r.full || r.display; url = r.url || null; }
    else {
      const shortAuthor = (r.author.split(/,| et al/i)[0].trim().split(/\s+/).slice(0, 2).join(" ")).slice(0, 20);
      label = `${shortAuthor} ${r.year}`;
      title = `${r.author} (${r.year}) — ${r.title}`;
      url = r.url || null;
    }
  }
  if (url) {
    return (
      <a href={url} target="_blank" rel="noreferrer" title={title} style={styles.citeTag} onClick={(e) => e.stopPropagation()} data-testid="cite-tag">
        {label}
      </a>
    );
  }
  return <span title={title} style={styles.citeTag} data-testid="cite-tag">{label}</span>;
}

// REFERENCES POPUP — a clickable, fully-cited source list (Growing_Systems_Database.xlsx).
// Rendered via a portal so the fixed overlay is never clipped by the sidebar's backdrop-filter.
export function ReferencesModal({ citations, title = "References & Data Sources", onClose }) {
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const [tierFilter, setTierFilter] = React.useState("all");
  const all = (citations || []).filter((c) => c && c.full).map((c) => ({ ...c, _tier: inferTier(c.url) }));
  const list = tierFilter === "all" ? all : all.filter((c) => c._tier === tierFilter);
  const TIER_CHIPS = [["all", "All"], ["peer-reviewed", "Peer-reviewed"], ["government", "Government"], ["standard", "Standard"], ["vendor", "Sample vendor"]];
  return createPortal(
    <div style={styles.refOverlay} data-testid="references-modal" onClick={onClose}>
      <div style={styles.refCard} onClick={(e) => e.stopPropagation()}>
        <div style={styles.refHeader}>
          <div>
            <div style={styles.refTitle}>{title}</div>
            <div style={styles.refSub}>{all.length} sources · every default is traceable to a real reference</div>
          </div>
          <button style={styles.refClose} data-testid="references-close" onClick={onClose} aria-label="Close references"><X size={16} /></button>
        </div>
        <div style={{ display: "flex", gap: 8, padding: "0 20px 2px", flexWrap: "wrap" }} data-testid="references-export-row">
          <span style={{ fontSize: 11.5, color: "#86868b", alignSelf: "center" }}>Vet the full source dossier (all metrics, every tier):</span>
          <button style={styles.refExportBtn} data-testid="references-export-html" onClick={downloadReferencesHTML}>
            <Download size={12} /> Download full dossier (HTML)
          </button>
          <button style={styles.refExportBtn} data-testid="references-export-csv" onClick={downloadReferencesCSV}>
            <Download size={12} /> CSV
          </button>
        </div>
        <div style={{ display: "flex", gap: 6, padding: "6px 20px 2px", flexWrap: "wrap" }} data-testid="references-tier-filter">
          {TIER_CHIPS.map(([key, label]) => {
            const n = key === "all" ? all.length : all.filter((c) => c._tier === key).length;
            const active = tierFilter === key;
            return (
              <button key={key} data-testid={`references-filter-${key}`} onClick={() => setTierFilter(key)}
                style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700, cursor: "pointer",
                  border: `1px solid ${active ? (key === "all" ? colors.textPrimary : TIER_META[key].color) : colors.border}`,
                  background: active ? (key === "all" ? colors.textPrimary : TIER_META[key].color) : "#fff",
                  color: active ? "#fff" : colors.textSecondary }}>
                {label} {n > 0 && <span style={{ opacity: 0.7 }}>{n}</span>}
              </button>
            );
          })}
        </div>
        <div style={styles.refBody}>
          {list.length === 0 && <div style={styles.refEmpty}>No sources in this tier.</div>}
          {list.map((c, i) => (
            <div key={c.tag || i} style={styles.refItem} data-testid={`reference-item-${i}`}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <div style={styles.refTag}>{c.tag}</div>
                <span style={{ fontSize: 9.5, fontWeight: 700, color: "#fff", background: TIER_META[c._tier].color, padding: "1px 7px", borderRadius: 999 }} data-testid={`reference-tier-${i}`}>{TIER_META[c._tier].label}</span>
              </div>
              <div style={styles.refFull}>{c.full}</div>
              {c.url ? (
                <a href={c.url} target="_blank" rel="noreferrer" style={styles.refLink} data-testid={`reference-link-${i}`}>
                  {c.url} <Upload size={11} style={{ transform: "rotate(45deg)" }} />
                </a>
              ) : (
                <span style={styles.refNoLink}>Sample vendor / project-team source — no public link</span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  );
}

// Button that opens the References popup for a given citation set (global KB list or per-system).
export function ReferencesButton({ citations, title, label = "References", testId = "references-button" }) {
  const [open, setOpen] = React.useState(false);
  const n = (citations || []).filter((c) => c && c.full).length;
  if (!n) return null;
  return (
    <>
      <button style={styles.refBtn} data-testid={testId} onClick={(e) => { e.stopPropagation(); setOpen(true); }}>
        <BadgeCheck size={13} /> {label} ({n})
      </button>
      {open && <ReferencesModal citations={citations} title={title} onClose={() => setOpen(false)} />}
    </>
  );
}
export function Metric({ label, value }) {
  return (
    <div style={styles.metric}>
      <div style={styles.metricLabel}>{label}</div>
      <div style={styles.metricValue}>{value}</div>
    </div>
  );
}
export function FooterStat({ label, value, accent }) {
  return (
    <div style={styles.footStat}>
      <div style={styles.footLabel}>{label}</div>
      <div style={{ ...styles.footValue, ...(accent ? { color: MINT } : {}) }}>{value}</div>
    </div>
  );
}
export function Hint({ children }) {
  return <div style={styles.hint}>{children}</div>;
}
export function OptionRow({ active, title, desc, tags, statusTag, statusOk, accent, onClick, testID, tip, icon: Icon, featured, recommended, dim, recReason, excluded, pending }) {
  const dimmed = dim && !active;
  if (pending) {
    return (
      <button data-testid={testID} disabled title="Data pending — excluded from calculations"
        style={{ ...styles.option, opacity: 0.45, cursor: "not-allowed", borderColor: colors.border, background: colors.surface, marginBottom: 8 }}>
        <span style={{ ...styles.check, borderColor: colors.borderStrong, background: "transparent" }} />
        {Icon && <span style={{ ...styles.optIcon, background: `${accent}10` }}><Icon size={15} color={colors.textTertiary} strokeWidth={2.2} /></span>}
        <span style={{ flex: 1, textAlign: "left" }}>
          <span style={{ ...styles.optTitle, color: colors.textTertiary }}>{title}</span>
          <span style={styles.optDesc}>Data pending — excluded from calculations</span>
        </span>
        <span style={{ ...styles.excludedPill, color: "#8a8a8f", background: "#8a8a8f18" }} data-testid={`${testID}-pending`}>Data pending</span>
      </button>
    );
  }
  if (excluded) {
    return (
      <button data-testid={testID} disabled title={recReason}
        style={{ ...styles.option, opacity: 0.42, cursor: "not-allowed", borderColor: colors.border, background: colors.surface, marginBottom: 8 }}>
        <span style={{ ...styles.check, borderColor: colors.borderStrong, background: "transparent" }} />
        {Icon && <span style={{ ...styles.optIcon, background: `${accent}10` }}><Icon size={15} color={colors.textTertiary} strokeWidth={2.2} /></span>}
        <span style={{ flex: 1, textAlign: "left" }}>
          <span style={{ ...styles.optTitle, color: colors.textTertiary }}>{title}</span>
          <span style={styles.optDesc}>{recReason || "Not available here"}</span>
        </span>
        <span style={styles.excludedPill} data-testid={`${testID}-excluded`}>Ground only</span>
      </button>
    );
  }
  return (
    <>
      <button onClick={onClick} data-testid={testID}
        style={{ ...styles.option, opacity: dimmed ? 0.5 : 1, borderColor: active ? accent : (featured ? `${accent}66` : colors.border), background: active ? "#fff" : (featured ? `${accent}0a` : colors.surface), boxShadow: active ? `0 2px 10px ${accent}22` : shadows.soft, marginBottom: active && tip ? 0 : 8 }}>
        <span style={{ ...styles.check, borderColor: active ? accent : colors.borderStrong, background: active ? accent : "transparent" }}>{active ? "✓" : ""}</span>
        {Icon && <span style={{ ...styles.optIcon, background: `${accent}14` }}><Icon size={15} color={accent} strokeWidth={2.2} /></span>}
        <span style={{ flex: 1, textAlign: "left" }}>
          <span style={{ ...styles.optTitle, color: active ? colors.textPrimary : colors.textSecondary }}>
            {title}
            {featured && <Star size={12} color="#ffb400" fill="#ffb400" strokeWidth={1.5} style={{ marginLeft: 6, verticalAlign: "-1px" }} data-testid={`${testID}-featured-star`} />}
          </span>
          {desc && <span style={styles.optDesc}>{desc}</span>}
          {tags && tags.length > 0 && (
            <span style={styles.tagRow}>{tags.map((t) => (<span key={t} style={styles.tag}>{t}</span>))}</span>
          )}
        </span>
        {recommended && !active && (
          <span style={styles.recPill} title={recReason} data-testid={`${testID}-recommended`}>Recommended</span>
        )}
        {featured && <span style={styles.featuredBadge}>Featured</span>}
        {statusTag && (
          <span style={{ ...styles.statusTag, color: statusOk ? colors.ok : colors.warn, background: statusOk ? "rgba(52,199,89,0.12)" : "rgba(255,159,10,0.14)" }}>{statusTag}</span>
        )}
      </button>
      {active && tip && (
        <div style={{ ...styles.optTip, borderLeft: `2px solid ${accent}` }} data-testid={`${testID}-tip`}>
          <span style={{ ...styles.optTipLabel, color: accent }}>Optimization path</span>
          {tip}
        </div>
      )}
    </>
  );
}
